/**
 * Hook pour gérer les notifications push PWA
 */

import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const usePushNotifications = (userId = null, userType = 'unknown') => {
  const [permission, setPermission] = useState('default');
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(false);
  const [supported, setSupported] = useState(false);
  const [error, setError] = useState(null);
  const [iosNeedsPwa, setIosNeedsPwa] = useState(false);

  // Vérifier le support
  useEffect(() => {
    const checkSupport = () => {
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
      const isIOSNonPwa = isIOS && !isStandalone;
      
      setIosNeedsPwa(isIOSNonPwa);
      
      const isSupported = 'serviceWorker' in navigator && 
                         'PushManager' in window && 
                         'Notification' in window;
      
      // Sur iOS hors PWA, le push ne fonctionne pas même si les APIs sont présentes
      setSupported(isSupported && !isIOSNonPwa);
      
      if (isSupported) {
        setPermission(Notification.permission);
      }
    };
    
    checkSupport();
  }, []);

  // Enregistrer le Service Worker
  const registerServiceWorker = useCallback(async () => {
    if (!supported) return null;
    
    try {
      const registration = await navigator.serviceWorker.register('/sw-push.js', { updateViaCache: 'none' });
      console.log('[Push] Service Worker registered');
      return registration;
    } catch (err) {
      console.error('[Push] SW registration failed:', err);
      setError('Erreur d\'enregistrement du Service Worker');
      return null;
    }
  }, [supported]);

  // Convertir la clé VAPID en Uint8Array
  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  // S'abonner aux notifications
  const subscribe = useCallback(async () => {
    if (!supported) {
      setError('Notifications non supportées sur ce navigateur');
      return false;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Vérifier si la permission est déjà accordée (peut avoir été demandée avant)
      const currentPerm = Notification.permission;
      if (currentPerm !== 'granted') {
        // Demander la permission (premier appel ou rappel)
        const perm = await Notification.requestPermission();
        setPermission(perm);
        
        if (perm !== 'granted') {
          setError('Permission refusée');
          setLoading(false);
          return false;
        }
      } else {
        setPermission('granted');
      }
      
      // Enregistrer le SW
      const registration = await registerServiceWorker();
      if (!registration) {
        setLoading(false);
        return false;
      }
      
      // Attendre que le SW soit prêt
      await navigator.serviceWorker.ready;
      
      // Récupérer la clé VAPID
      const { data: vapidData } = await api.get('/api/push/vapid-public-key');
      const applicationServerKey = urlBase64ToUint8Array(vapidData.publicKey);
      
      // S'abonner
      const pushSubscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey
      });
      
      // Envoyer l'abonnement au backend
      await api.post('/api/push/subscribe', {
        subscription: pushSubscription.toJSON(),
        userId,
        userType
      });
      
      setSubscription(pushSubscription);
      console.log('[Push] Subscribed successfully');
      setLoading(false);
      return true;
      
    } catch (err) {
      console.error('[Push] Subscription error:', err);
      setError(err.message || 'Erreur lors de l\'abonnement');
      setLoading(false);
      return false;
    }
  }, [supported, registerServiceWorker, userId, userType]);

  // Se désabonner
  const unsubscribe = useCallback(async () => {
    if (!subscription) return true;
    
    setLoading(true);
    
    try {
      await subscription.unsubscribe();
      
      await api.post('/api/push/unsubscribe', {
        endpoint: subscription.endpoint
      });
      
      setSubscription(null);
      setLoading(false);
      return true;
      
    } catch (err) {
      console.error('[Push] Unsubscribe error:', err);
      setError(err.message);
      setLoading(false);
      return false;
    }
  }, [subscription]);

  // Vérifier l'abonnement existant
  useEffect(() => {
    const checkExistingSubscription = async () => {
      if (!supported) return;
      
      try {
        const registration = await navigator.serviceWorker.getRegistration('/sw-push.js');
        if (registration) {
          const existingSub = await registration.pushManager.getSubscription();
          if (existingSub) {
            setSubscription(existingSub);
          }
        }
      } catch (err) {
        console.log('[Push] No existing subscription');
      }
    };
    
    checkExistingSubscription();
  }, [supported]);

  return {
    supported,
    permission,
    subscription,
    isSubscribed: !!subscription,
    loading,
    error,
    subscribe,
    unsubscribe,
    iosNeedsPwa
  };
};

export default usePushNotifications;
